import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Autocomplete, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import API from '../../../services/api';
import ProductCreateInline from './ProductCreateInline';
import InventoryManagementDialog from '../../dialogs/InventoryManagementDialog';
import MobileDialog from '../../dialogs/MobileDialog';
import BarcodeScannerDialog from '../../dialogs/BarcodeScannerDialog';

/**
 * Lean "add products to an existing order" dialog that mirrors the
 * single-page new-order flow:
 *  - product autocomplete (with inline "מוצר חדש" button)
 *  - qty / price / storage row with the same fast keyboard chain
 *  - inline low-stock warning + inline InventoryManagementDialog (take/add)
 *  - accumulates a temporary "items to add" list (numbered, removable)
 *  - on submit, calls onSubmit(addedItems) which the parent maps to
 *    its own /order/update/ items_to_add payload
 *
 * Props:
 *  - open
 *  - onClose
 *  - submitting           : parent's mutation isLoading
 *  - allProducts          : preloaded product list (parent's query)
 *  - allStorages          : preloaded storage list
 *  - storageEnabled       : group flag
 *  - productLabel         : i18n label
 *  - productPluralLabel   : i18n label
 *  - productFields        : uiConfig.product_fields (for inline create)
 *  - productFieldsSchema  : uiConfig.product_fields_schema (for inline create)
 *  - onSubmit             : (addedItems) => void  (called when user clicks "הוסף להזמנה")
 *  - onProductsRefetch    : optional callback to invalidate the products query after inline create
 */
const fetchProductDetails = async (productId) => {
  const { data } = await API.get('/product/', { params: { ProductID: productId } });
  return data;
};

// MUI Autocomplete's default filterOptions only matches against the
// label returned by getOptionLabel (ProductName + CatalogNumber).
// Barcode isn't in the label, so typing a barcode would get filtered
// out client-side. Match across every searchable field.
const productFilterOptions = (options, { inputValue }) => {
  const q = (inputValue || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((opt) => {
    const hay = [opt.ProductName, opt.CatalogNumber, opt.Barcode, opt.ProductID]
      .filter((v) => v != null && v !== '')
      .map((v) => String(v).toLowerCase());
    return hay.some((s) => s.includes(q));
  });
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const AddProductsToOrderDialog = ({
  open,
  onClose,
  submitting = false,
  allProducts = [],
  allStorages = [],
  storageEnabled = false,
  barcodeEnabled = false,
  productLabel = 'מוצר',
  productPluralLabel = 'מוצרים',
  productFields = [],
  productFieldsSchema = {},
  onSubmit,
  onProductsRefetch,
  // Role gates mirror the ProductsPage inventory_take_roles /
  // inventory_add_roles config — default true for backward-compat with
  // callers that don't pass them. When the user lacks adjust rights
  // we show a Hebrew prompt instead of opening the inventory dialog.
  canTakeInventory = true,
  canAddInventory = true,
}) => {
  const canAdjustInventory = canTakeInventory || canAddInventory;
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState('');
  const [productUnitPrice, setProductUnitPrice] = useState('');
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [availableStorages, setAvailableStorages] = useState([]);
  const [noInventoryWarning, setNoInventoryWarning] = useState(false);

  const [addedItems, setAddedItems] = useState([]);
  const [error, setError] = useState('');

  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [inventoryAdjustOpen, setInventoryAdjustOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Role-aware opener — if the user lacks adjust permission, surface a
  // Hebrew prompt on the shared error Alert instead of opening the
  // inventory dialog. Same UI-level gate we use on the order Edit dialog.
  const requestInventoryAdjust = () => {
    if (!canAdjustInventory) {
      setError('יש לפנות למנהל לעדכון המלאי');
      return;
    }
    setInventoryAdjustOpen(true);
  };

  const productAutocompleteRef = useRef(null);
  const quantityRef = useRef(null);
  const unitPriceRef = useRef(null);
  const addButtonRef = useRef(null);

  // Reset state every time the dialog opens
  useEffect(() => {
    if (open) {
      setSelectedProduct(null);
      setProductQuantity('');
      setProductUnitPrice('');
      setSelectedStorage(null);
      setAvailableStorages([]);
      setNoInventoryWarning(false);
      setAddedItems([]);
      setError('');
      setTimeout(() => productAutocompleteRef.current?.focus(), 150);
    }
  }, [open]);

  const handleProductSelect = async (_, newValue) => {
    if (!newValue) {
      setSelectedProduct(null);
      setProductUnitPrice('');
      setAvailableStorages([]);
      setSelectedStorage(null);
      setNoInventoryWarning(false);
      return;
    }
    setError('');
    setNoInventoryWarning(false);
    setSelectedProduct(newValue);

    if (storageEnabled) {
      try {
        const full = await fetchProductDetails(newValue.ProductID);
        setSelectedProduct(full);
        setProductUnitPrice(String(full.Price ?? ''));
        if (full.inventory && full.inventory.length > 0) {
          const withStock = full.inventory.filter((i) => i.available_quantity > 0);
          if (withStock.length === 0) {
            setNoInventoryWarning(true);
            setAvailableStorages([]);
            setSelectedStorage(null);
          } else {
            setAvailableStorages(withStock);
            const def = withStock.find((i) => i.is_default) || withStock[0];
            setSelectedStorage(def);
          }
        } else {
          setNoInventoryWarning(true);
          setAvailableStorages([]);
          setSelectedStorage(null);
        }
      } catch (e) {
        setError('שגיאה בטעינת פרטי המוצר');
      }
    } else {
      setProductUnitPrice(String(newValue.Price ?? ''));
    }

    if (!productQuantity) setProductQuantity('1');
    setTimeout(() => {
      quantityRef.current?.focus();
      quantityRef.current?.select?.();
    }, 150);
  };

  const handleAddRow = () => {
    if (!selectedProduct) return;
    setError('');
    if (storageEnabled && noInventoryWarning) {
      setError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
      return;
    }
    const qty = parseFloat(productQuantity);
    if (!qty || qty <= 0) {
      setError('נא להזין כמות');
      return;
    }
    const price = parseFloat(productUnitPrice) || selectedProduct.Price;
    const isCustom = Math.abs(Number(price) - Number(selectedProduct.Price)) > 0.001;

    if (storageEnabled && selectedStorage) {
      if (qty > selectedStorage.available_quantity) {
        setError(`הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${selectedStorage.available_quantity})`);
        return;
      }
    }

    // Merge with existing same product/price/storage in the local list
    const existingIdx = addedItems.findIndex((entry) => {
      const sameProduct = entry.product.ProductID === selectedProduct.ProductID;
      const samePrice =
        (!entry.customPrice && !isCustom) ||
        (entry.customPrice && isCustom && Math.abs(Number(entry.customPrice) - Number(price)) < 0.001);
      const sameStorage = storageEnabled
        ? entry.storageId === (selectedStorage?.StorageID || null)
        : true;
      return sameProduct && samePrice && sameStorage;
    });

    if (existingIdx >= 0) {
      setAddedItems((prev) => {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: Math.round((parseFloat(updated[existingIdx].quantity) + qty) * 100) / 100,
        };
        return updated;
      });
    } else {
      setAddedItems((prev) => [
        ...prev,
        {
          product: selectedProduct,
          quantity: Math.round(qty * 100) / 100,
          customPrice: isCustom ? price : null,
          storageId: selectedStorage?.StorageID || null,
          storageName: selectedStorage?.StorageName || null,
          entryId: Date.now(),
        },
      ]);
    }

    setSelectedProduct(null);
    setProductQuantity('');
    setProductUnitPrice('');
    setSelectedStorage(null);
    setAvailableStorages([]);
    setNoInventoryWarning(false);
    setError('');
    setTimeout(() => productAutocompleteRef.current?.focus(), 50);
  };

  const handleScannedProductAdd = async ({ product, quantity: qty, unitPrice: price }) => {
    if (!product) return;
    setError('');
    let full = product;
    let storage = null;

    if (storageEnabled) {
      try {
        full = await fetchProductDetails(product.ProductID);
      } catch (e) {
        setError('שגיאה בטעינת פרטי המוצר');
        return;
      }
      const withStock = (full.inventory || []).filter((i) => i.available_quantity > 0);
      if (withStock.length === 0) {
        setError(`לא ניתן להוסיף ${productLabel} ללא מלאי. יש לעדכן את המלאי תחילה.`);
        return;
      }
      storage = withStock.find((i) => i.is_default) || withStock[0];
      if (qty > storage.available_quantity) {
        setError(`הכמות המבוקשת (${qty}) עולה על המלאי הזמין (${storage.available_quantity})`);
        return;
      }
    }

    const resolvedPrice = Number.isFinite(price) ? price : Number(full.Price) || 0;
    const isCustom = Math.abs(resolvedPrice - Number(full.Price)) > 0.001;

    setAddedItems((prev) => {
      const existingIdx = prev.findIndex((entry) => {
        const sameProduct = entry.product.ProductID === full.ProductID;
        const samePrice =
          (!entry.customPrice && !isCustom) ||
          (entry.customPrice && isCustom && Math.abs(Number(entry.customPrice) - resolvedPrice) < 0.001);
        const sameStorage = storageEnabled ? entry.storageId === (storage?.StorageID || null) : true;
        return sameProduct && samePrice && sameStorage;
      });
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: Math.round((parseFloat(updated[existingIdx].quantity) + qty) * 100) / 100,
        };
        return updated;
      }
      return [
        ...prev,
        {
          product: full,
          quantity: Math.round(qty * 100) / 100,
          customPrice: isCustom ? resolvedPrice : null,
          storageId: storage?.StorageID || null,
          storageName: storage?.StorageName || null,
          entryId: Date.now(),
        },
      ];
    });
  };

  const handleProductCreated = (enhancedProduct, defaultStorage) => {
    setSelectedProduct(enhancedProduct);
    setProductUnitPrice(String(enhancedProduct.Price ?? ''));
    if (storageEnabled) {
      const withStock = (enhancedProduct.inventory || []).filter(
        (i) => i.available_quantity > 0
      );
      if (withStock.length > 0) {
        setAvailableStorages(withStock);
        setSelectedStorage(
          defaultStorage || withStock.find((i) => i.is_default) || withStock[0]
        );
        setNoInventoryWarning(false);
      } else {
        // The user created the product without seeding any inventory — fire
        // the warning so they can bump it inline via InventoryManagementDialog.
        setAvailableStorages([]);
        setSelectedStorage(null);
        setNoInventoryWarning(true);
      }
    }
    onProductsRefetch?.();
    if (!productQuantity) setProductQuantity('1');
    setTimeout(() => quantityRef.current?.focus(), 150);
  };

  const handleInventoryAdjusted = async () => {
    if (!selectedProduct) return;
    try {
      const refreshed = await fetchProductDetails(selectedProduct.ProductID);
      setSelectedProduct(refreshed);
      if (refreshed.inventory && refreshed.inventory.length > 0) {
        const withStock = refreshed.inventory.filter((i) => i.available_quantity > 0);
        if (withStock.length > 0) {
          setAvailableStorages(withStock);
          const samePicked = selectedStorage
            ? withStock.find((i) => i.StorageID === selectedStorage.StorageID)
            : null;
          setSelectedStorage(samePicked || withStock.find((i) => i.is_default) || withStock[0]);
          setNoInventoryWarning(false);
        }
      }
      setError('');
      setTimeout(() => quantityRef.current?.focus(), 100);
    } catch (e) {
      setError('שגיאה ברענון פרטי המוצר');
    }
  };

  const handleSubmit = () => {
    if (addedItems.length === 0) return;
    onSubmit?.(addedItems);
  };

  const totalRows = addedItems.length;

  return (
    <>
      <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '60vh' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
            <AddIcon color="primary" />
            <Typography variant="h6" component="span">
              הוספת {productPluralLabel} להזמנה
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
          {/* Top-of-card alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, '& .MuiAlert-message': { width: '100%', py: 1 } }}>
              <Typography variant="body2" sx={{ mb: storageEnabled && selectedProduct && selectedStorage ? 1.5 : 0 }}>
                {error}
              </Typography>
              {storageEnabled && selectedProduct && selectedStorage && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                  <Button
                    color="error"
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => requestInventoryAdjust()}
                    sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                  >
                    עדכן מלאי
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    variant="text"
                    onClick={() => setError('')}
                    sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                  >
                    סגור
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          {storageEnabled && noInventoryWarning && selectedProduct && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 3, '& .MuiAlert-message': { width: '100%', py: 1 } }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                אין מלאי זמין ל<strong>{selectedProduct?.ProductName || productLabel}</strong>.
                לחץ על הכפתור כדי להוסיף מלאי במחסן ולהמשיך.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  color="warning"
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => requestInventoryAdjust()}
                  sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                >
                  עדכן מלאי
                </Button>
              </Box>
            </Alert>
          )}

          {/* Add-row section — wrapped in a subtle Paper with a section label
              so the user always sees where the input area starts. */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: 'action.hover' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                הוספת שורה חדשה
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {barcodeEnabled && (
                  <Tooltip title="סריקת ברקוד">
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={() => setScannerOpen(true)}
                      sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                    >
                      סרוק
                    </Button>
                  </Tooltip>
                )}
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateProductOpen(true)}
                  sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                >
                  {`${productLabel} חדש`}
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: storageEnabled
                    ? 'minmax(220px, 2.4fr) 100px 130px minmax(150px, 1fr) auto'
                    : 'minmax(220px, 2.4fr) 100px 130px auto',
                },
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
            >
              <Autocomplete
                options={allProducts}
                value={selectedProduct}
                onChange={handleProductSelect}
                getOptionLabel={(o) =>
                  o ? `${o.ProductName}${o.CatalogNumber ? ` (${o.CatalogNumber})` : ''}` : ''
                }
                filterOptions={productFilterOptions}
                isOptionEqualToValue={(o, v) => o?.ProductID === v?.ProductID}
                noOptionsText={`לא נמצאו ${productPluralLabel}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`חיפוש ${productLabel}`}
                    size="small"
                    inputRef={productAutocompleteRef}
                  />
                )}
              />
              <TextField
                label="כמות"
                type="number"
                size="small"
                inputRef={quantityRef}
                value={productQuantity}
                onChange={(e) => setProductQuantity(e.target.value)}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    unitPriceRef.current?.focus();
                    unitPriceRef.current?.select?.();
                  }
                }}
                sx={{
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none', margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
              <TextField
                label="מחיר"
                type="number"
                size="small"
                inputRef={unitPriceRef}
                value={productUnitPrice}
                onChange={(e) => setProductUnitPrice(e.target.value)}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (storageEnabled && noInventoryWarning && selectedProduct) {
                      requestInventoryAdjust();
                      return;
                    }
                    addButtonRef.current?.focus();
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': { direction: 'ltr' },
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none', margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
                InputProps={{ endAdornment: <InputAdornment position="end">₪</InputAdornment> }}
              />
              {storageEnabled && (
                <FormControl size="small" disabled={!selectedProduct || availableStorages.length === 0}>
                  <InputLabel>מחסן</InputLabel>
                  <Select
                    label="מחסן"
                    value={selectedStorage?.StorageID || ''}
                    onChange={(e) => {
                      const s = availableStorages.find((x) => x.StorageID === e.target.value);
                      setSelectedStorage(s || null);
                    }}
                  >
                    {availableStorages.map((s) => (
                      <MenuItem key={s.StorageID} value={s.StorageID}>
                        {s.StorageName} ({s.available_quantity})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                ref={addButtonRef}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRow();
                  }
                }}
                disabled={!selectedProduct || (storageEnabled && noInventoryWarning)}
                sx={{
                  direction: 'ltr',
                  whiteSpace: 'nowrap',
                  height: 40,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                הוסף
              </Button>
            </Box>
          </Paper>

          {/* Pending items section */}
          {addedItems.length > 0 ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  פריטים להוספה ({addedItems.length})
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: storageEnabled ? 520 : 440 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }}>#</TableCell>
                      <TableCell>{productLabel}</TableCell>
                      {storageEnabled && <TableCell>מחסן</TableCell>}
                      <TableCell align="center">כמות</TableCell>
                      <TableCell align="center">מחיר יחידה</TableCell>
                      <TableCell align="center">סה"כ</TableCell>
                      <TableCell sx={{ width: 48 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addedItems.map((entry, idx) => {
                      const price = parseFloat(entry.customPrice ?? entry.product.Price) || 0;
                      const lineTotal = price * (parseFloat(entry.quantity) || 0);
                      return (
                        <TableRow key={entry.entryId} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {entry.product.ProductName}
                            </Typography>
                          </TableCell>
                          {storageEnabled && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {entry.storageName || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center">{entry.quantity}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <Typography variant="body2">{formatCurrency(price)}</Typography>
                              {entry.customPrice != null && (
                                <Tooltip title={`מחיר בסיס: ${formatCurrency(entry.product.Price)}`}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ textDecoration: 'line-through' }}
                                  >
                                    {formatCurrency(entry.product.Price)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(lineTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setAddedItems((prev) => prev.filter((_, i) => i !== idx))
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'text.secondary',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                עדיין לא נוספו {productPluralLabel}.
              </Typography>
              <Typography variant="caption">
                בחר {productLabel}, הזן כמות ומחיר ולחץ "הוסף".
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>ביטול</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || totalRows === 0}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ direction: 'ltr', whiteSpace: 'nowrap', minWidth: 200 }}
          >
            {submitting
              ? 'מוסיף...'
              : totalRows === 0
              ? `הוסף להזמנה`
              : `הוסף ${totalRows} ${totalRows === 1 ? productLabel : productPluralLabel} להזמנה`}
          </Button>
        </DialogActions>
      </MobileDialog>

      <ProductCreateInline
        open={createProductOpen}
        onClose={() => setCreateProductOpen(false)}
        productLabel={productLabel}
        productFields={productFields}
        productFieldsSchema={productFieldsSchema}
        storageEnabled={storageEnabled}
        allStorages={allStorages}
        onCreated={handleProductCreated}
      />

      <InventoryManagementDialog
        open={inventoryAdjustOpen}
        onClose={() => setInventoryAdjustOpen(false)}
        product={selectedProduct}
        storages={allStorages}
        initialStorageId={
          selectedStorage?.StorageID ||
          allStorages.find((s) => s.is_default)?.StorageID ||
          allStorages[0]?.StorageID ||
          null
        }
        onSuccess={handleInventoryAdjusted}
        canTakeInventory={canTakeInventory}
        canAddInventory={canAddInventory}
      />

      {barcodeEnabled && (
        <BarcodeScannerDialog
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          // autoAdd + closeOnAdd: each scan appends to the pending
          // items list (qty=1, merges duplicates) and then closes
          // the scanner so the operator sees the new row land in
          // the table below. Without closeOnAdd, the camera stays
          // open and the only visible confirmation is a small
          // banner — operators kept scanning the same item 4–5
          // times thinking nothing had registered.
          autoAdd
          closeOnAdd
          onAdd={handleScannedProductAdd}
          productLabel={productLabel}
        />
      )}
    </>
  );
};

export default AddProductsToOrderDialog;
